<script setup lang="ts">
  import { computed } from 'vue'
  import { Button } from '@/components/ui/button'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    coercePhoneAiMinuteCount,
    getPhoneAiProgressBarFillClass,
    getPhoneAiUsageBarPercent,
    isPhoneAiBeginnerPlan,
    isPhoneAiEnterprisePlan
  } from '@/utils/phoneAiUsage'

  const props = withDefaults(
    defineProps<{
      /** Omitted by some API payloads until first usage sync — coerce to 0 */
      usedMinutes?: number
      includedMinutes?: number
      /** `phonebot_self_service_plan` — drives bar fill colours (beginner vs pro-style). */
      plan?: string | null
    }>(),
    {
      usedMinutes: 0,
      includedMinutes: 0,
      plan: null
    }
  )

  const emit = defineEmits<{
    (e: 'upgrade-plan'): void
  }>()

  const { t } = useTranslation({
    en: {
      minutesUsedSuffix: 'minutes used this month',
      marieActive: 'Marie is active',
      upgradePlan: 'Upgrade plan'
    },
    de: {
      minutesUsedSuffix: 'Minuten diesen Monat genutzt',
      marieActive: 'Marie ist aktiv',
      upgradePlan: 'Tarif aufwerten'
    },
    fr: {
      minutesUsedSuffix: 'minutes utilisées ce mois-ci',
      marieActive: 'Marie est active',
      upgradePlan: 'Mettre à niveau le forfait'
    }
  })

  const showUpgradeButton = computed(() => isPhoneAiBeginnerPlan(props.plan))

  const safeUsedMinutes = computed(() => coercePhoneAiMinuteCount(props.usedMinutes))
  const safeIncludedMinutes = computed(() => coercePhoneAiMinuteCount(props.includedMinutes))

  const barPercent = computed(() =>
    getPhoneAiUsageBarPercent(safeUsedMinutes.value, safeIncludedMinutes.value)
  )

  const fillClass = computed(() =>
    getPhoneAiProgressBarFillClass(props.plan, safeUsedMinutes.value, safeIncludedMinutes.value)
  )

  /** Beginner keeps used/included; professional tiers and enterprise show used only. */
  const showIncludedInMinutesLabel = computed(() => isPhoneAiBeginnerPlan(props.plan))

  const showUsageBar = computed(() => !isPhoneAiEnterprisePlan(props.plan))

  const minutesLeadText = computed(() => {
    if (showIncludedInMinutesLabel.value) {
      return `${safeUsedMinutes.value}/${safeIncludedMinutes.value}`
    }
    return String(safeUsedMinutes.value)
  })

  const ariaLabel = computed(() => {
    const suffix = t('minutesUsedSuffix')
    if (showIncludedInMinutesLabel.value) {
      return `${safeUsedMinutes.value}/${safeIncludedMinutes.value} ${suffix}`
    }
    return `${safeUsedMinutes.value} ${suffix}`
  })
</script>

<template>
  <div class="rounded-xl border border-gray-200 bg-white p-4">
    <div class="flex flex-col gap-3">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          <span class="relative flex h-2.5 w-2.5 items-center justify-center">
            <span
              class="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-20"
            ></span>
            <span class="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400"></span>
          </span>
          <p class="text-base font-semibold text-gray-900">{{ t('marieActive') }}</p>
        </div>
        <Button
          v-if="showUpgradeButton"
          variant="secondary"
          size="sm"
          class="h-8 border-gray-200 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
          @click="emit('upgrade-plan')"
        >
          {{ t('upgradePlan') }}
        </Button>
      </div>

      <p class="text-sm text-gray-600">
        <span class="font-semibold text-gray-900">
          {{ minutesLeadText }}
        </span>
        {{ t('minutesUsedSuffix') }}
      </p>

      <div
        v-if="showUsageBar"
        class="relative h-2.5 w-full overflow-hidden rounded-full bg-gray-100"
        role="progressbar"
        :aria-valuenow="Math.round(barPercent)"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-label="ariaLabel"
      >
        <div
          class="h-full max-w-full rounded-full transition-[width] duration-300 ease-out"
          :class="[fillClass, 'min-w-0']"
          :style="{ width: `${barPercent}%` }"
        />
      </div>
    </div>
  </div>
</template>
